import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import { endpoints } from "../../lib/api";
import { saveVoiceChunk, getVoiceChunksBySession, deleteVoiceChunk } from "../../lib/db";

// Long field walkthroughs auto-split into ~90s segments so no single upload
// ever gets huge, and nothing is lost if one segment's transcription fails.
const CHUNK_MS = 90_000;
const RESTORATION_PROMPT =
    "Restoration industry terminology: flood cut, baseboard, toe kick, drywall, carpet and pad, " +
    "Cat 1, Cat 2, Cat 3 water category, Class 1, Class 2, Class 3, Class 4, dehu, dehumidifier, " +
    "air mover, air scrubber, negative air, antimicrobial, containment, full gut, recon, " +
    "Xactimate, LF, SF.";

export function VoiceDictation({ onTranscript, disabled, sessionId }) {
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [pending, setPending] = useState(0);
    const [offlineQueued, setOfflineQueued] = useState(0);
    const [savedCount, setSavedCount] = useState(0);
    const [finalizing, setFinalizing] = useState(false);
    const [error, setError] = useState("");

    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const curChunkPartsRef = useRef([]);
    const chunkTimerRef = useRef(null);
    const secTimerRef = useRef(null);
    const nextIndexRef = useRef(0);
    const partsRef = useRef({});
    const expectedRef = useRef(0);
    const stoppingRef = useRef(false);
    const queueRef = useRef([]);
    const drainingRef = useRef(false);
    const recoveringRef = useRef(false);
    const runIdRef = useRef(null);
    const liveChunkIdsRef = useRef(new Set());
    const onlineHandlerRef = useRef(null);

    /* Quick-capture offline durability: on mount (and whenever the browser
     * comes back online), drain any dictation audio that failed to transcribe
     * in a PREVIOUS visit to this same ROM job while offline — e.g. the
     * estimator lost signal in a basement, backgrounded/closed the app, and
     * reopened it later. Scoped to this job's sessionId so recovered text
     * never leaks into a different ROM's chat. Chunks still owned by the
     * currently-live recording (tracked in `liveChunkIdsRef`) are skipped
     * here — the existing in-memory `drainQueue` already handles those,
     * avoiding a double-transcribe race on reconnect. */
    async function loadSavedCount() {
        if (!sessionId) return;
        try { setSavedCount((await getVoiceChunksBySession(sessionId)).length); } catch { /* ignore */ }
    }

    async function recoverPersisted() {
        if (!sessionId || recoveringRef.current) return;
        recoveringRef.current = true;
        try {
            const all = await getVoiceChunksBySession(sessionId);
            const stored = all.filter((c) => !liveChunkIdsRef.current.has(c.id));
            if (!stored.length || !navigator.onLine) { setSavedCount(all.length); return; }
            const collected = [];
            for (const item of stored) {
                try {
                    const text = await attemptTranscribe(item.blob);
                    if (text) collected.push(text);
                    await deleteVoiceChunk(item.id);
                } catch {
                    break; // still failing — leave the rest queued, retry on the next 'online' event
                }
            }
            await loadSavedCount();
            const combined = collected.join(" ").trim();
            if (combined) onTranscript(combined, { recovered: true });
        } finally {
            recoveringRef.current = false;
        }
    }

    useEffect(() => {
        if (!sessionId) return;
        loadSavedCount();
        recoverPersisted();
        const handler = () => recoverPersisted();
        window.addEventListener("online", handler);
        return () => window.removeEventListener("online", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    async function persistChunk(index, blob) {
        if (!sessionId) return;
        const id = `${sessionId}:${runIdRef.current}:${index}`;
        liveChunkIdsRef.current.add(id);
        try { await saveVoiceChunk({ id, sessionId, index, blob, created_at: Date.now() }); } catch { /* ignore */ }
        loadSavedCount();
    }

    useEffect(() => () => {
        clearInterval(chunkTimerRef.current);
        clearInterval(secTimerRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (onlineHandlerRef.current) window.removeEventListener("online", onlineHandlerRef.current);
    }, []);

    function finishIfDone() {
        if (expectedRef.current === 0 || Object.keys(partsRef.current).length < expectedRef.current) return;
        const text = Object.keys(partsRef.current)
            .map(Number).sort((a, b) => a - b)
            .map((i) => partsRef.current[i]).filter(Boolean).join(" ").trim();
        partsRef.current = {};
        expectedRef.current = 0;
        nextIndexRef.current = 0;
        stoppingRef.current = false;
        setFinalizing(false);
        if (text) onTranscript(text);
        else setError("Didn't catch any speech — try again.");
    }

    async function attemptTranscribe(blob) {
        const formData = new FormData();
        formData.append("file", blob, "dictation.webm");
        formData.append("prompt", RESTORATION_PROMPT);
        const { data } = await endpoints.transcribeVoice(formData);
        return data?.text || "";
    }

    async function transcribeChunk(index, blob) {
        setPending((p) => p + 1);
        try {
            let text;
            try {
                text = await attemptTranscribe(blob);
            } catch (e) {
                if (!navigator.onLine) throw e;
                await new Promise((r) => setTimeout(r, 1200));
                text = await attemptTranscribe(blob);
            }
            partsRef.current[index] = text;
            if (sessionId) deleteVoiceChunk(`${sessionId}:${runIdRef.current}:${index}`).catch(() => {});
        } catch (e) {
            if (!navigator.onLine) {
                queueRef.current.push({ index, blob });
                setOfflineQueued(queueRef.current.length);
                persistChunk(index, blob);
            } else {
                partsRef.current[index] = "";
                setError("One voice segment failed to transcribe — the rest were captured.");
                if (sessionId) deleteVoiceChunk(`${sessionId}:${runIdRef.current}:${index}`).catch(() => {});
            }
        } finally {
            setPending((p) => Math.max(0, p - 1));
            finishIfDone();
        }
    }

    async function drainQueue() {
        if (drainingRef.current) return;
        drainingRef.current = true;
        try {
            while (queueRef.current.length && navigator.onLine) {
                const { index, blob } = queueRef.current.shift();
                setOfflineQueued(queueRef.current.length);
                await transcribeChunk(index, blob);
            }
        } finally {
            drainingRef.current = false;
        }
    }

    function enqueueChunk(blob) {
        const index = nextIndexRef.current++;
        if (!navigator.onLine) {
            queueRef.current.push({ index, blob });
            setOfflineQueued(queueRef.current.length);
            persistChunk(index, blob);
            finishIfDone();
            return;
        }
        transcribeChunk(index, blob);
    }

    function armChunkRecorder(stream) {
        const mimeType = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
        const opts = mimeType ? { mimeType, audioBitsPerSecond: 32000 } : { audioBitsPerSecond: 32000 };
        const recorder = new MediaRecorder(stream, opts);
        curChunkPartsRef.current = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) curChunkPartsRef.current.push(e.data); };
        recorder.onstop = () => {
            const blob = new Blob(curChunkPartsRef.current, { type: recorder.mimeType || "audio/webm" });
            curChunkPartsRef.current = [];
            if (blob.size > 0) enqueueChunk(blob);
            if (stoppingRef.current) {
                expectedRef.current = nextIndexRef.current;
                finishIfDone();
            } else {
                armChunkRecorder(stream);
                recorderRef.current.start();
            }
        };
        recorderRef.current = recorder;
    }

    async function start() {
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            stoppingRef.current = false;
            nextIndexRef.current = 0;
            runIdRef.current = (crypto.randomUUID && crypto.randomUUID()) || `run-${Date.now()}`;
            partsRef.current = {};
            expectedRef.current = 0;
            queueRef.current = [];
            setOfflineQueued(0);
            armChunkRecorder(stream);
            recorderRef.current.start();
            chunkTimerRef.current = setInterval(() => {
                if (recorderRef.current?.state === "recording") recorderRef.current.stop();
            }, CHUNK_MS);
            setElapsed(0);
            let sec = 0;
            secTimerRef.current = setInterval(() => setElapsed(++sec), 1000);
            onlineHandlerRef.current = () => drainQueue();
            window.addEventListener("online", onlineHandlerRef.current);
            setRecording(true);
        } catch (e) {
            setError("Microphone access denied — check your browser permissions.");
        }
    }

    function stop() {
        if (stoppingRef.current) return;
        stoppingRef.current = true;
        clearInterval(chunkTimerRef.current);
        clearInterval(secTimerRef.current);
        setRecording(false);
        setFinalizing(true);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        } else {
            // A rotation's own stop() was already in flight — its onstop will
            // see stoppingRef=true and finalize. Safety net only, in case it
            // genuinely never fires.
            setTimeout(() => { if (expectedRef.current === 0) { expectedRef.current = nextIndexRef.current; finishIfDone(); } }, 300);
        }
    }

    const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
    const ss = String(elapsed % 60).padStart(2, "0");

    return (
        <div className="relative self-end">
            {(recording || finalizing || offlineQueued > 0 || error || savedCount > 0) && (
                <div className="absolute bottom-full right-0 mb-2 max-w-[min(78vw,320px)] rounded-[6px] border px-2.5 py-1.5 text-[11px] shadow-sm flex items-center gap-1.5"
                    style={{ background: "white", borderColor: "var(--rom-border)" }} data-testid="voice-dictation-status">
                    {recording && (
                        <span className="flex items-center gap-1.5 font-semibold text-red-600" data-testid="voice-dictation-recording-indicator">
                            <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> Recording… {mm}:{ss}
                        </span>
                    )}
                    {finalizing && (
                        <span className="flex items-center gap-1.5 text-[var(--rom-text-2)]" data-testid="voice-dictation-finalizing">
                            <CircleNotch size={11} className="animate-spin" /> Transcribing{pending ? ` (${pending})` : ""}…
                        </span>
                    )}
                    {offlineQueued > 0 && (
                        <span className="text-amber-700" data-testid="voice-dictation-offline-queue">
                            {offlineQueued} segment{offlineQueued === 1 ? "" : "s"} queued — offline, will send when back online
                        </span>
                    )}
                    {!recording && !finalizing && offlineQueued === 0 && savedCount > 0 && (
                        <span className="text-amber-700" data-testid="voice-dictation-saved-offline">
                            {navigator.onLine
                                ? `Sending ${savedCount} saved segment${savedCount === 1 ? "" : "s"}…`
                                : `${savedCount} segment${savedCount === 1 ? "" : "s"} saved offline — will send once you're back online`}
                        </span>
                    )}
                    {error && (
                        <span className="flex items-center gap-1 text-red-600" data-testid="voice-dictation-error">
                            <WarningCircle size={11} weight="bold" /> {error}
                        </span>
                    )}
                </div>
            )}
            <button
                type="button"
                onClick={recording ? stop : start}
                disabled={disabled || finalizing}
                title={recording ? "Stop recording" : "Dictate a walkthrough"}
                className={`h-14 w-14 sm:h-10 sm:w-10 rounded-[8px] border flex items-center justify-center transition-colors shrink-0 ${
                    recording ? "bg-red-600 border-red-600 text-white animate-pulse"
                        : "border-[var(--rom-border)] text-[var(--rom-text-3)] hover:border-[var(--rom-navy)] hover:text-[var(--rom-navy)] disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
                data-testid="ai-voice-mic"
            >
                {finalizing ? <CircleNotch size={20} className="animate-spin sm:hidden" /> : recording ? <Stop size={20} weight="fill" className="sm:hidden" /> : <Microphone size={20} weight="bold" className="sm:hidden" />}
                {finalizing ? <CircleNotch size={16} className="animate-spin hidden sm:block" /> : recording ? <Stop size={16} weight="fill" className="hidden sm:block" /> : <Microphone size={16} weight="bold" className="hidden sm:block" />}
            </button>
        </div>
    );
}
